// src/features/media/videoFilters.ts
//
// Pure predicate + (de)serialization logic for the videos advanced-search
// filters. No React or engine imports — unit-tested per ADR-004. The
// search-param helpers ship now so filter state can later move to typed URL
// params (mirroring src/features/analytics/searchParams.ts) or saved searches
// without touching the panel or engine.

import { NEEDS_ATTENTION_STATUSES } from '@/lib/constants/media';
import type { VideoFilters } from '@/lib/types/Media';

/** Subset of the grid engine's dataset record that the predicates read. */
export interface VideoFilterCandidate {
  /** Lowercased tag names (matching is case-insensitive). */
  lowerTags: string[];
  published: number;
  duration: number;
  state: string;
  status: string;
}

const NEEDS_ATTENTION_SET: ReadonlySet<string> = new Set(NEEDS_ATTENTION_STATUSES);

/** Comma is a TAG_SPLIT_CHARS separator, so tag names can never contain it. */
const TAG_PARAM_SEPARATOR = ',';

function parseTagsParam(value: unknown): string[] {
  if (typeof value !== 'string' || value.length === 0) return [];
  return value
    .split(TAG_PARAM_SEPARATOR)
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

function parseSecondsParam(value: unknown): number | null {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isInteger(parsed) || parsed < 0) return null;
  return parsed;
}

function parseStateParam(value: unknown): 'active' | 'inactive' | null {
  return value === 'active' || value === 'inactive' ? value : null;
}

export class VideoFilterUtils {
  /** Number of active filter dimensions (drives the toolbar badge). */
  static countActive(filters: VideoFilters): number {
    let count = 0;
    if (filters.tagsAll.length > 0) count += 1;
    if (filters.tagsAny.length > 0) count += 1;
    if (filters.tagsNone.length > 0) count += 1;
    if (filters.publishedAfter !== null || filters.publishedBefore !== null) count += 1;
    if (filters.durationMin !== null || filters.durationMax !== null) count += 1;
    if (filters.state !== null) count += 1;
    if (filters.needsAttention) count += 1;
    return count;
  }

  static isEmpty(filters: VideoFilters): boolean {
    return this.countActive(filters) === 0;
  }

  /** Lowercase the tag buckets once so per-record matching stays cheap. */
  static prepare(filters: VideoFilters): VideoFilters {
    const lower = (tags: string[]) => tags.map((tag) => tag.toLowerCase());
    return {
      ...filters,
      tagsAll: lower(filters.tagsAll),
      tagsAny: lower(filters.tagsAny),
      tagsNone: lower(filters.tagsNone),
    };
  }

  /** All predicates AND together. Expects `prepare()`d filters. */
  static matchesFilters(candidate: VideoFilterCandidate, filters: VideoFilters): boolean {
    return (
      this.matchesTags(candidate.lowerTags, filters) &&
      this.matchesPublished(candidate.published, filters) &&
      this.matchesDuration(candidate.duration, filters) &&
      this.matchesState(candidate.state, filters) &&
      this.matchesAttention(candidate.status, filters)
    );
  }

  static matchesTags(lowerTags: string[], filters: VideoFilters): boolean {
    for (const tag of filters.tagsAll) {
      if (!lowerTags.includes(tag)) return false;
    }
    if (filters.tagsAny.length > 0 && !filters.tagsAny.some((tag) => lowerTags.includes(tag))) {
      return false;
    }
    for (const tag of filters.tagsNone) {
      if (lowerTags.includes(tag)) return false;
    }
    return true;
  }

  /** Inclusive start, exclusive end (repo range convention). */
  static matchesPublished(published: number, filters: VideoFilters): boolean {
    if (filters.publishedAfter !== null && published < filters.publishedAfter) return false;
    if (filters.publishedBefore !== null && published >= filters.publishedBefore) return false;
    return true;
  }

  /** Inclusive min and max. */
  static matchesDuration(duration: number, filters: VideoFilters): boolean {
    if (filters.durationMin !== null && duration < filters.durationMin) return false;
    if (filters.durationMax !== null && duration > filters.durationMax) return false;
    return true;
  }

  static matchesState(state: string, filters: VideoFilters): boolean {
    return filters.state === null || state === filters.state;
  }

  static matchesAttention(status: string, filters: VideoFilters): boolean {
    return !filters.needsAttention || NEEDS_ATTENTION_SET.has(status);
  }

  /** Canonical key for memoizing filter result sets (tag order-insensitive). */
  static cacheKey(filters: VideoFilters): string {
    const norm = (tags: string[]) => tags.map((tag) => tag.toLowerCase()).sort();
    return JSON.stringify([
      norm(filters.tagsAll),
      norm(filters.tagsAny),
      norm(filters.tagsNone),
      filters.publishedAfter,
      filters.publishedBefore,
      filters.durationMin,
      filters.durationMax,
      filters.state,
      filters.needsAttention,
    ]);
  }

  /** Serialize active filters to flat URL-style params (inactive keys omitted). */
  static filtersToSearchParams(filters: VideoFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters.tagsAll.length > 0) params.tagsAll = filters.tagsAll.join(TAG_PARAM_SEPARATOR);
    if (filters.tagsAny.length > 0) params.tagsAny = filters.tagsAny.join(TAG_PARAM_SEPARATOR);
    if (filters.tagsNone.length > 0) params.tagsNone = filters.tagsNone.join(TAG_PARAM_SEPARATOR);
    if (filters.publishedAfter !== null) params.pubAfter = String(filters.publishedAfter);
    if (filters.publishedBefore !== null) params.pubBefore = String(filters.publishedBefore);
    if (filters.durationMin !== null) params.durMin = String(filters.durationMin);
    if (filters.durationMax !== null) params.durMax = String(filters.durationMax);
    if (filters.state !== null) params.state = filters.state;
    if (filters.needsAttention) params.attention = '1';
    return params;
  }

  /** Tolerant inverse of `filtersToSearchParams` — invalid values degrade to inactive. */
  static searchParamsToFilters(params: Record<string, unknown>): VideoFilters {
    return {
      tagsAll: parseTagsParam(params.tagsAll),
      tagsAny: parseTagsParam(params.tagsAny),
      tagsNone: parseTagsParam(params.tagsNone),
      publishedAfter: parseSecondsParam(params.pubAfter),
      publishedBefore: parseSecondsParam(params.pubBefore),
      durationMin: parseSecondsParam(params.durMin),
      durationMax: parseSecondsParam(params.durMax),
      state: parseStateParam(params.state),
      needsAttention: params.attention === '1',
    };
  }
}
